import type { IClass } from "../models/class";

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

export function getDayName(date: Date): string {
  return DAY_NAMES[date.getDay()];
}

/** Set time on a date from "HH:MM" or "HH:MM:SS" string (local time). */
export function setTimeOnDate(date: Date, timeStr: string): Date {
  const parts = timeStr.trim().split(":");
  const hours = Number.parseInt(parts[0] ?? "0", 10);
  const minutes = parts.length >= 2 ? Number.parseInt(parts[1] ?? "0", 10) : 0;
  const seconds = parts.length >= 3 ? Number.parseInt(parts[2] ?? "0", 10) : 0;
  const d = new Date(date);
  d.setHours(hours, minutes, seconds, 0);
  return d;
}

/** Get class session start/end for a given date from class schedule. Returns null if no schedule for that day. */
export function getClassSessionForDate(
  classItem: Pick<IClass, "schedule">,
  date: Date,
): { sessionStart: Date; sessionEnd: Date } | null {
  const schedule = classItem.schedule;
  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    return null;
  }
  const dayName = getDayName(date).toLowerCase();
  const slot = schedule.find(
    s => s?.day?.toLowerCase() === dayName && s?.isActive !== false,
  );
  if (!slot?.startTime || !slot?.endTime) {
    return null;
  }
  const dayOnly = new Date(date);
  dayOnly.setHours(0, 0, 0, 0);
  const sessionStart = setTimeOnDate(dayOnly, slot.startTime);
  const sessionEnd = setTimeOnDate(dayOnly, slot.endTime);
  return { sessionStart, sessionEnd };
}

/** Start and end of the given date (local day bounds). */
export function getDayBounds(date: Date): { start: Date; end: Date } {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Parse "YYYY-MM-DD" as UTC date and return day-of-week in UTC (0=Sunday, ..., 6=Saturday).
 */
function getUTCDayName(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const utcDate = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  return DAY_NAMES[utcDate.getUTCDay()];
}

/**
 * Set time on a date in UTC from "HH:MM" or "HH:MM:SS" string.
 * Date is interpreted as UTC calendar date (year, month, day from dateStr).
 */
function setTimeOnDateUTC(dateStr: string, timeStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const parts = timeStr.trim().split(":");
  const hours = Number.parseInt(parts[0] ?? "0", 10);
  const minutes = parts.length >= 2 ? Number.parseInt(parts[1] ?? "0", 10) : 0;
  const seconds = parts.length >= 3 ? Number.parseInt(parts[2] ?? "0", 10) : 0;
  return new Date(
    Date.UTC(y, (m ?? 1) - 1, d ?? 1, hours, minutes, seconds, 0),
  );
}

/**
 * Get class session start/end for a given UTC calendar date ("YYYY-MM-DD").
 * Uses schedule startTime/endTime as UTC. Returns null if no schedule for that day.
 */
export function getClassSessionForDateUTC(
  classItem: Pick<IClass, "schedule">,
  dateStr: string,
): { sessionStart: Date; sessionEnd: Date } | null {
  const schedule = classItem.schedule;
  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    return null;
  }
  const dayName = getUTCDayName(dateStr).toLowerCase();
  const slot = schedule.find(
    (s: { day?: string; isActive?: boolean }) =>
      s?.day?.toLowerCase() === dayName && s?.isActive !== false,
  );
  if (!slot?.startTime || !slot?.endTime) {
    return null;
  }
  const sessionStart = setTimeOnDateUTC(dateStr, slot.startTime);
  let sessionEnd = setTimeOnDateUTC(dateStr, slot.endTime);
  if (sessionEnd.getTime() <= sessionStart.getTime()) {
    sessionEnd = new Date(sessionEnd.getTime() + 24 * 60 * 60 * 1000);
  }
  return { sessionStart, sessionEnd };
}

/** Start and end of the given UTC date ("YYYY-MM-DD") as Date objects. */
export function getUTCDayBounds(dateStr: string): { start: Date; end: Date } {
  const [y, m, d] = dateStr.split("-").map(Number);
  const start = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0));
  const end = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999));
  return { start, end };
}
